import React, { useState, useEffect, useMemo } from "react";
import { 
  Plus, 
  Trash2, 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Printer, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Receipt,
  Sparkles, 
  ArrowDownLeft, 
  ArrowUpRight, 
  AlertCircle,
  ShieldCheck,
  RefreshCw
} from "lucide-react";
import Button from "../../components/common/Button";
import { Modal } from "../../components/common/Modal";
import { Input } from "../../components/common/Input";
import { 
  fetchMonthlyTLedger, 
  createLedgerEntry, 
  deleteLedgerEntry, 
  MonthlyTLedgerResponse, 
  LedgerItem
} from "../../services/jobService";

const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`cht-bg-white cht-border cht-border-gray-200/80 cht-rounded-[24px] cht-shadow-[0_10px_30px_rgba(0,0,0,0.03)] ${className}`}>
    {children}
  </div>
);

export const AdminMonthlyLedger: React.FC = () => {
  // Current month state (defaults to current YYYY-MM)
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  });

  const [ledgerData, setLedgerData] = useState<MonthlyTLedgerResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  // Modal State
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"EXPENSE" | "INCOME">("EXPENSE");
  const [itemName, setItemName] = useState<string>("");
  const [itemAmount, setItemAmount] = useState<string>("");
  const [itemDate, setItemDate] = useState<string>("");
  const [itemNotes, setItemNotes] = useState<string>("");

  // Delete Prompt Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number; name: string } | null>(null);

  // Status / Error Prompt Modal State
  const [promptMessage, setPromptMessage] = useState<{ title: string; message: string; type: "error" | "info" } | null>(null);

  // Load ledger data with strict personal scoping
  const loadData = async (monthToFetch: string) => {
    try {
      setLoading(true);
      const res = await fetchMonthlyTLedger(monthToFetch, "personal");
      setLedgerData(res);
    } catch (err) {
      console.error("Failed to load personal monthly ledger", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(selectedMonth);
  }, [selectedMonth]);

  // Month navigation helpers
  const handlePrevMonth = () => {
    const [yearStr, monStr] = selectedMonth.split("-");
    let year = parseInt(yearStr, 10);
    let mon = parseInt(monStr, 10) - 1;
    if (mon < 1) {
      mon = 12;
      year -= 1;
    }
    setSelectedMonth(`${year}-${String(mon).padStart(2, "0")}`);
  };

  const handleNextMonth = () => {
    const [yearStr, monStr] = selectedMonth.split("-");
    let year = parseInt(yearStr, 10);
    let mon = parseInt(monStr, 10) + 1;
    if (mon > 12) {
      mon = 1;
      year += 1;
    }
    setSelectedMonth(`${year}-${String(mon).padStart(2, "0")}`);
  };

  const monthFormattedTitle = useMemo(() => {
    try {
      const [year, mon] = selectedMonth.split("-");
      const d = new Date(parseInt(year, 10), parseInt(mon, 10) - 1, 1);
      return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    } catch {
      return selectedMonth;
    }
  }, [selectedMonth]);

  // Open modal with pre-configured type
  const openModal = (type: "EXPENSE" | "INCOME") => {
    setModalType(type);
    setItemName("");
    setItemAmount("");
    setItemNotes("");
    
    const today = new Date().toISOString().split("T")[0];
    if (today.startsWith(selectedMonth)) {
      setItemDate(today);
    } else {
      setItemDate(`${selectedMonth}-01`);
    }
    setModalOpen(true);
  };

  // Submit new entry
  const handleSubmitEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemName || !itemAmount) return;

    try {
      setActionLoading(true);
      await createLedgerEntry({
        entry_type: modalType,
        item_name: itemName,
        amount: itemAmount,
        date: itemDate || `${selectedMonth}-01`,
        notes: itemNotes,
      });
      setModalOpen(false);
      loadData(selectedMonth);
    } catch (err) {
      console.error("Failed to add entry", err);
      setPromptMessage({
        title: "Save Failed",
        message: "Failed to record entry. Please verify amounts and network connection.",
        type: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Prompt delete modal
  const handlePromptDelete = (id: number | string, name: string) => {
    if (typeof id !== "number") return;
    setItemToDelete({ id, name });
    setDeleteModalOpen(true);
  };

  // Confirm delete manual entry
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setActionLoading(true);
      await deleteLedgerEntry(itemToDelete.id);
      setDeleteModalOpen(false);
      setItemToDelete(null);
      loadData(selectedMonth);
    } catch (err: any) {
      console.error("Failed to delete entry", err);
      setPromptMessage({
        title: "Delete Denied",
        message: err?.response?.data?.detail || "You can only delete ledger records you personally entered.",
        type: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Trigger browser print
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700 cht-pb-16 print:cht-p-0 print:cht-space-y-4">
      {/* Top Header & Monthly Controls */}
      <header className="cht-flex cht-flex-col lg:cht-flex-row cht-items-start lg:cht-items-center cht-justify-between cht-gap-4 print:cht-hidden">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5">
            <div className="cht-p-2 cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-rounded-xl">
              <Receipt size={22} />
            </div>
            <div>
              <div className="cht-flex cht-items-center cht-gap-2">
                <h1 className="cht-text-2xl lg:cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">
                  Personal Income & Expenditure
                </h1>
                <span className="cht-px-2.5 cht-py-0.5 cht-bg-blue-50 cht-text-[#3e57da] cht-border cht-border-blue-100 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase">
                  My Records
                </span>
              </div>
              <p className="cht-text-xs cht-text-gray-500 cht-font-medium cht-mt-0.5">
                Two-column ledger tracking your personal inflows, outflows, and verified service commissions.
              </p>
            </div>
          </div>
        </div>

        {/* Month Selector & Actions */}
        <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2.5">
          {/* Month Stepper */}
          <div className="cht-flex cht-items-center cht-bg-white cht-border cht-border-gray-200 cht-rounded-2xl cht-p-1 cht-shadow-sm">
            <button
              onClick={handlePrevMonth}
              title="Previous Month"
              className="cht-p-2 cht-text-gray-500 hover:cht-text-[#3e57da] hover:cht-bg-gray-100 cht-rounded-xl cht-transition-all"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="cht-px-3 cht-flex cht-items-center cht-gap-2">
              <Calendar size={14} className="cht-text-[#3e57da]" />
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="cht-text-xs cht-font-black cht-text-gray-800 cht-bg-transparent cht-border-none cht-outline-none cursor-pointer"
              />
            </div>
            <button
              onClick={handleNextMonth}
              title="Next Month"
              className="cht-p-2 cht-text-gray-500 hover:cht-text-[#3e57da] hover:cht-bg-gray-100 cht-rounded-xl cht-transition-all"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Action Buttons */}
          <Button
            variant="outline"
            leftIcon={<Plus size={16} />}
            onClick={() => openModal("EXPENSE")}
            className="cht-rounded-2xl cht-border-rose-200 cht-text-rose-700 hover:cht-bg-rose-50 cht-text-xs"
          >
            + Expense
          </Button>

          <Button
            variant="outline"
            leftIcon={<Plus size={16} />}
            onClick={() => openModal("INCOME")}
            className="cht-rounded-2xl cht-border-emerald-200 cht-text-emerald-700 hover:cht-bg-emerald-50 cht-text-xs"
          >
            + Income
          </Button>

          <Button
            variant="secondary"
            leftIcon={<Printer size={16} />}
            onClick={handlePrint}
            className="cht-rounded-2xl cht-text-xs"
          >
            Print Sheet
          </Button>
        </div>
      </header>

      {/* Top 3 Metric Cards */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-5 print:cht-grid-cols-3">
        {/* Expenses Card */}
        <GlassCard className="cht-p-5 cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 cht-bg-rose-50 cht-text-rose-600 cht-rounded-2xl">
            <ArrowDownLeft size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              My Expenses (Outflows)
            </p>
            <h3 className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-0.5">
              ₦{Number(ledgerData?.totals?.total_expenses || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </h3>
            <span className="cht-text-[10px] cht-font-semibold cht-text-gray-500">
              Manual expenses recorded by you
            </span>
          </div>
        </GlassCard>

        {/* Incomes Card */}
        <GlassCard className="cht-p-5 cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl">
            <ArrowUpRight size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              My Incomes (Inflows)
            </p>
            <h3 className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-0.5">
              ₦{Number(ledgerData?.totals?.total_incomes || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </h3>
            <span className="cht-text-[10px] cht-font-semibold cht-text-gray-500">
              Manual incomes recorded by you
            </span>
          </div>
        </GlassCard>

        {/* Net Surplus / Deficit Card */}
        <GlassCard className="cht-p-5 cht-flex cht-items-center cht-gap-4">
          <div className={`cht-p-3.5 cht-rounded-2xl ${
            ledgerData?.totals?.is_surplus ? "cht-bg-emerald-50 cht-text-emerald-600" : "cht-bg-rose-50 cht-text-rose-600"
          }`}>
            <DollarSign size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Net {ledgerData?.totals?.is_surplus ? "Surplus" : "Deficit"}
            </p>
            <h3 className={`cht-text-xl cht-font-black cht-mt-0.5 ${
              ledgerData?.totals?.is_surplus ? "cht-text-emerald-600" : "cht-text-rose-600"
            }`}>
              {ledgerData?.totals?.is_surplus ? "+" : ""}₦{Number(ledgerData?.totals?.net_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </h3>
            <span className="cht-text-[10px] cht-font-semibold cht-text-gray-400">
              Personal Period: {monthFormattedTitle}
            </span>
          </div>
        </GlassCard>
      </div>

      {/* ── TWO-COLUMN NOTEBOOK SHEET ────────────────────────────────────────── */}
      <div className="cht-bg-white cht-rounded-[28px] cht-border-2 cht-border-gray-300 cht-shadow-md cht-overflow-hidden print:cht-border print:cht-rounded-none">
        {/* Notebook Title Bar */}
        <div className="cht-py-5 cht-border-b-2 cht-border-gray-300 cht-text-center cht-bg-gradient-to-r cht-from-gray-50/70 cht-via-white cht-to-gray-50/70">
          <h2 className="cht-text-2xl cht-font-black cht-text-gray-900 cht-tracking-wide cht-font-serif">
            {monthFormattedTitle}
          </h2>
          <p className="cht-text-[11px] cht-font-bold cht-text-gray-400 cht-tracking-widest cht-uppercase cht-mt-0.5">
            Personal Income & Expenditure Ledger Statement
          </p>
        </div>

        {/* 2-Column Split: Left = Expenses | Right = Incomes */}
        <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-divide-y md:cht-divide-y-0 md:cht-divide-x-2 cht-divide-gray-300">
          {/* ── LEFT COLUMN: EXPENSES ─────────────────────────────────────────── */}
          <div className="cht-flex cht-flex-col cht-justify-between cht-bg-[#fafbfe]/30">
            <div>
              {/* Column Header */}
              <div className="cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-4 cht-bg-red-600 cht-text-white cht-border-b-2 cht-border-red-700">
                <span className="cht-text-sm cht-font-black cht-tracking-wider cht-uppercase">
                  Expenses / Outflows
                </span>
                <span className="cht-text-base cht-font-black">₦</span>
              </div>

              {/* Rows List */}
              <div className="cht-divide-y cht-divide-gray-100/90">
                {ledgerData?.expenses && ledgerData.expenses.length > 0 ? (
                  ledgerData.expenses.map((exp: LedgerItem, idx: number) => (
                    <div
                      key={`exp-${exp.id}-${idx}`}
                      className="cht-group cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-3 hover:cht-bg-rose-50/20 cht-transition-colors"
                    >
                      <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
                        <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-w-12 cht-shrink-0">
                          {exp.date}
                        </span>
                        <div className="cht-truncate">
                          <span className="cht-text-sm cht-font-bold cht-text-gray-800">
                            {exp.item}
                          </span>
                          {exp.is_system && (
                            <span className="cht-ml-2 cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-bg-amber-50 cht-text-amber-700 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase">
                              <Sparkles size={10} />
                              Commission
                            </span>
                          )}
                          {exp.notes && (
                            <span className="cht-block cht-text-[11px] cht-text-gray-400 cht-truncate">
                              {exp.notes}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="cht-flex cht-items-center cht-gap-3 cht-shrink-0">
                        <span className="cht-text-sm cht-font-black cht-text-gray-900 cht-tabular-nums">
                          {Number(exp.amount).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                        </span>
                        {!exp.is_system && (
                          <button
                            onClick={() => handlePromptDelete(exp.id, exp.item)}
                            title="Delete entry"
                            className="cht-opacity-0 group-hover:cht-opacity-100 cht-p-1.5 cht-text-gray-400 hover:cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-lg cht-transition-all print:cht-hidden cursor-pointer"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="cht-py-12 cht-text-center cht-text-xs cht-text-gray-400 cht-font-medium">
                    No personal expense entries recorded for this month.
                    <div className="cht-mt-2 print:cht-hidden">
                      <Button
                        variant="outline"
                        onClick={() => openModal("EXPENSE")}
                        className="cht-rounded-xl cht-text-[11px] cht-py-1 cht-px-3"
                      >
                        + Add Expense
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Left Column Subtotal */}
            <div className="cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-4 cht-border-t-2 cht-border-gray-300 cht-bg-gray-50/80 cht-mt-4">
              <span className="cht-text-xs cht-font-black cht-text-gray-700 cht-uppercase cht-tracking-wider">
                Total Expenses:
              </span>
              <span className="cht-text-base cht-font-black cht-text-rose-700 cht-tabular-nums">
                ₦{Number(ledgerData?.totals?.total_expenses || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          {/* ── RIGHT COLUMN: INCOMES ─────────────────────────────────────────── */}
          <div className="cht-flex cht-flex-col cht-justify-between cht-bg-white">
            <div>
              {/* Column Header */}
              <div className="cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-4 cht-bg-emerald-600 cht-text-white cht-border-b-2 cht-border-emerald-700">
                <span className="cht-text-sm cht-font-black cht-tracking-wider cht-uppercase">
                  Incomes / Inflows
                </span>
                <span className="cht-text-base cht-font-black">₦</span>
              </div>

              {/* Rows List */}
              <div className="cht-divide-y cht-divide-gray-100/90">
                {ledgerData?.incomes && ledgerData.incomes.length > 0 ? (
                  ledgerData.incomes.map((inc: LedgerItem, idx: number) => (
                    <div
                      key={`inc-${inc.id}-${idx}`}
                      className="cht-group cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-3 hover:cht-bg-emerald-50/20 cht-transition-colors"
                    >
                      <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
                        <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-w-12 cht-shrink-0">
                          {inc.date}
                        </span>
                        <div className="cht-truncate">
                          <span className="cht-text-sm cht-font-bold cht-text-gray-800">
                            {inc.item}
                          </span>
                          {inc.is_system && (
                            <span className="cht-ml-2 cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase">
                              <Sparkles size={10} />
                              Fulfillment
                            </span>
                          )}
                          {inc.notes && (
                            <span className="cht-block cht-text-[11px] cht-text-gray-400 cht-truncate">
                              {inc.notes}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="cht-flex cht-items-center cht-gap-3 cht-shrink-0">
                        <span className="cht-text-sm cht-font-black cht-text-gray-900 cht-tabular-nums">
                          {Number(inc.amount).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                        </span>
                        {!inc.is_system && (
                          <button
                            onClick={() => handlePromptDelete(inc.id, inc.item)}
                            title="Delete entry"
                            className="cht-opacity-0 group-hover:cht-opacity-100 cht-p-1.5 cht-text-gray-400 hover:cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-lg cht-transition-all print:cht-hidden cursor-pointer"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="cht-py-12 cht-text-center cht-text-xs cht-text-gray-400 cht-font-medium">
                    No personal income entries recorded for this month.
                    <div className="cht-mt-2 print:cht-hidden">
                      <Button
                        variant="outline"
                        onClick={() => openModal("INCOME")}
                        className="cht-rounded-xl cht-text-[11px] cht-py-1 cht-px-3"
                      >
                        + Add Income
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column Subtotal */}
            <div className="cht-flex cht-items-center cht-justify-between cht-px-6 cht-py-4 cht-border-t-2 cht-border-gray-300 cht-bg-gray-50/80 cht-mt-4">
              <span className="cht-text-xs cht-font-black cht-text-gray-700 cht-uppercase cht-tracking-wider">
                Total Incomes:
              </span>
              <span className="cht-text-base cht-font-black cht-text-emerald-700 cht-tabular-nums">
                ₦{Number(ledgerData?.totals?.total_incomes || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* ── FOOTER NET BALANCE BAR ────────────────────────────────────────── */}
        <div className={`cht-px-8 cht-py-5 cht-border-t-2 cht-border-gray-300 cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-justify-between cht-gap-3 ${
          ledgerData?.totals?.is_surplus ? "cht-bg-emerald-100/70" : "cht-bg-rose-100/70"
        }`}>
          <div>
            <span className="cht-text-xs cht-font-black cht-text-gray-800 cht-uppercase cht-tracking-widest">
              Personal Net Operating Balance
            </span>
            <h4 className="cht-text-sm cht-font-bold cht-text-gray-600">
              {ledgerData?.totals?.is_surplus ? "Net Monthly Surplus" : "Net Monthly Deficit"}
            </h4>
          </div>

          <div className="cht-text-right">
            <span className={`cht-text-2xl lg:cht-text-3xl cht-font-black cht-tabular-nums ${
              ledgerData?.totals?.is_surplus ? "cht-text-emerald-800" : "cht-text-rose-800"
            }`}>
              {ledgerData?.totals?.is_surplus ? "+" : ""}₦{Number(ledgerData?.totals?.net_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* ── MODAL: ADD EXPENSE OR INCOME ENTRY ──────────────────────────────── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        size="md"
        icon={modalType === "EXPENSE" ? <Receipt size={22} className="cht-text-rose-600" /> : <TrendingUp size={22} className="cht-text-emerald-600" />}
        title={`Add Personal ${modalType === "EXPENSE" ? "Expense (Outflow)" : "Income (Inflow)"}`}
        subtitle={`Record an entry in your personal ${modalType === "EXPENSE" ? "outflows" : "inflows"} for ${monthFormattedTitle}.`}
      >
        <div className="cht-space-y-4">
          {/* Type Switcher Pills */}
          <div className="cht-flex cht-items-center cht-justify-between cht-p-1.5 cht-bg-gray-100 cht-rounded-2xl">
            <span className="cht-text-xs cht-font-bold cht-text-gray-500 cht-pl-2">Entry Type:</span>
            <div className="cht-flex cht-gap-1">
              <button
                type="button"
                onClick={() => setModalType("EXPENSE")}
                className={`cht-px-4 cht-py-1.5 cht-rounded-xl cht-text-xs cht-font-black cht-transition-all cursor-pointer ${
                  modalType === "EXPENSE" ? "cht-bg-rose-600 cht-text-white cht-shadow-sm" : "cht-text-gray-600 hover:cht-text-gray-900"
                }`}
              >
                Expense
              </button>
              <button
                type="button"
                onClick={() => setModalType("INCOME")}
                className={`cht-px-4 cht-py-1.5 cht-rounded-xl cht-text-xs cht-font-black cht-transition-all cursor-pointer ${
                  modalType === "INCOME" ? "cht-bg-emerald-600 cht-text-white cht-shadow-sm" : "cht-text-gray-600 hover:cht-text-gray-900"
                }`}
              >
                Income
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmitEntry} className="cht-space-y-4">
            <Input
              label="Item Description"
              required
              placeholder={modalType === "EXPENSE" ? "e.g. Data Subscription, Materials, Transport" : "e.g. Consultation Fee, Direct Client Print"}
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              leftIcon={modalType === "EXPENSE" ? <Receipt size={17} className="cht-text-rose-500" /> : <TrendingUp size={17} className="cht-text-emerald-500" />}
            />

            <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
              <Input
                label="Amount"
                type="number"
                required
                min="0"
                step="0.01"
                placeholder="e.g. 15000"
                value={itemAmount}
                onChange={(e) => setItemAmount(e.target.value)}
                prefixText="₦"
              />

              <Input
                label="Date"
                type="date"
                required
                value={itemDate}
                onChange={(e) => setItemDate(e.target.value)}
                leftIcon={<Calendar size={17} className="cht-text-gray-400" />}
              />
            </div>

            <Input
              label="Notes or Reference"
              subLabel="Optional"
              placeholder="e.g. Receipt #124, direct cash"
              value={itemNotes}
              onChange={(e) => setItemNotes(e.target.value)}
            />

            <div className="cht-flex cht-items-center cht-justify-end cht-gap-2.5 cht-pt-3 cht-border-t cht-border-gray-100">
              <Button
                variant="outline"
                type="button"
                onClick={() => setModalOpen(false)}
                className="cht-rounded-xl cht-text-xs"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={actionLoading}
                className={`cht-rounded-xl cht-text-xs ${
                  modalType === "EXPENSE" ? "cht-bg-rose-600 hover:cht-bg-rose-700" : "cht-bg-emerald-600 hover:cht-bg-emerald-700"
                }`}
              >
                {actionLoading ? "Saving..." : `Save ${modalType === "EXPENSE" ? "Expense" : "Income"}`}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* ── MODAL: DELETE CONFIRMATION PROMPT ────────────────────────────────── */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (!actionLoading) {
            setDeleteModalOpen(false);
            setItemToDelete(null);
          }
        }}
        size="sm"
        icon={<Trash2 size={22} className="cht-text-rose-600" />}
        title="Confirm Removal"
        subtitle="Remove personal ledger entry"
        footer={
          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2.5 cht-w-full">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setDeleteModalOpen(false);
                setItemToDelete(null);
              }}
              disabled={actionLoading}
              className="cht-rounded-xl cht-text-xs"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleConfirmDelete}
              disabled={actionLoading}
              className="cht-rounded-xl cht-text-xs cht-bg-rose-600 hover:cht-bg-rose-700 cht-text-white"
            >
              {actionLoading ? "Removing..." : "Yes, Remove"}
            </Button>
          </div>
        }
      >
        <div className="cht-py-2">
          <p className="cht-text-sm cht-text-gray-700 cht-font-medium">
            Are you sure you want to remove <span className="cht-font-black cht-text-gray-900">"{itemToDelete?.name}"</span> from your personal ledger?
          </p>
          <p className="cht-text-xs cht-text-gray-400 cht-mt-2">
            This entry was recorded by you and will be permanently removed.
          </p>
        </div>
      </Modal>

      {/* ── MODAL: STATUS / ERROR PROMPT ────────────────────────────────────── */}
      <Modal
        isOpen={!!promptMessage}
        onClose={() => setPromptMessage(null)}
        size="sm"
        icon={<AlertCircle size={22} className={promptMessage?.type === "error" ? "cht-text-rose-600" : "cht-text-[#3e57da]"} />}
        title={promptMessage?.title || "Notification"}
        subtitle="System notification"
        footer={
          <div className="cht-flex cht-justify-end cht-w-full">
            <Button
              variant="primary"
              type="button"
              onClick={() => setPromptMessage(null)}
              className="cht-rounded-xl cht-text-xs"
            >
              Dismiss
            </Button>
          </div>
        }
      >
        <p className="cht-text-sm cht-text-gray-700 cht-font-medium cht-py-2">
          {promptMessage?.message}
        </p>
      </Modal>
    </div>
  );
};

export default AdminMonthlyLedger;
